import { SyntheticEvent, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';

import { useQuery } from '@redwoodjs/web';

import { InputBaseField } from 'src/components/common/form/FormInput';
import { NPIAutoComplete } from 'src/components/FormikComponents';
import MainCard from 'src/components/MainCard/MainCard';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { GET_MCCS } from 'src/graphql/queries/mccs.query';
import { SalesConditionsType } from 'src/hooks/usePricings';

interface PricingSalesConditionsProps {
    salesConditions?: SalesConditionsType;
    gpvConditions?: SalesConditionsType;
    mccConditions?: SalesConditionsType;
    updatePricingConditions?: (fieldName: string, fieldValue: SalesConditionsType) => void;
}

export const PricingSalesConditions = ({
    salesConditions,
    gpvConditions,
    mccConditions,
    updatePricingConditions,
}: PricingSalesConditionsProps) => {
    const { isPartner } = useAuthContext();
    const [expanded, setExpanded] = useState<string | false>('panel1');

    const handleChange = (panel: string) => (_event: SyntheticEvent, newExpanded: boolean) => {
        setExpanded(newExpanded ? panel : false);
    };

    const [mccData, setMccData] = useState([]);
    useQuery(GET_MCCS, {
        onCompleted: (data) => {
            setMccData(
                data?.mccs?.data?.map((mcc) => ({
                    key: mcc.id,
                    value: `${mcc.code} - ${mcc.name}`,
                    label: `${mcc.code} - ${mcc.name}`,
                }))
            );
        },
    });

    return (
        <MainCard>
            <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
                <AccordionSummary
                    sx={{
                        backgroundColor: '#FADABB',
                        color: '#1D1D1B',
                        borderRadius: '20px 20px 0 0',
                    }}
                    expandIcon={<ExpandMoreIcon sx={{ color: '#1D1D1B' }} />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
                            Sales Conditions
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        border: 'thin solid #FADABB',
                        marginTop: '-20px',
                        padding: '30px 20px 20px 20px',
                        borderRadius: '0 0 20px 20px',
                    }}
                >
                    <Box>
                        <Grid container>
                            <Grid item xs={6} md={6}>
                                <Box sx={{ marginLeft: '20px' }}>
                                    <Typography variant="h3" sx={{ margin: '30px 0 0px 0' }}>
                                        Add Sales Conditions?
                                    </Typography>

                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            aria-labelledby="salesConditions-billed-radio-buttons"
                                            name="salesConditions"
                                            row
                                            defaultValue={salesConditions}
                                        >
                                            <FormControlLabel
                                                value={SalesConditionsType.YES}
                                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                                label="Yes"
                                                onClick={() => {
                                                    updatePricingConditions(
                                                        'salesConditions',
                                                        SalesConditionsType.YES
                                                    );
                                                }}
                                            />
                                            <FormControlLabel
                                                value={SalesConditionsType.NO}
                                                control={<Radio sx={{ color: '#F39E4E' }} />}
                                                label="No"
                                                onClick={() => {
                                                    updatePricingConditions(
                                                        'salesConditions',
                                                        SalesConditionsType.NO
                                                    );
                                                }}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Box>
                                {salesConditions === SalesConditionsType.YES && (
                                    <Box sx={{ marginLeft: '20px' }}>
                                        <Typography variant="h3" sx={{ margin: '30px 0 0px 0' }}>
                                            Specific Monthly Turnover? GPV
                                        </Typography>

                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                aria-labelledby="gpvConditions-billed-radio-buttons"
                                                name="gpvConditions"
                                                row
                                                defaultValue={gpvConditions}
                                            >
                                                <FormControlLabel
                                                    value={SalesConditionsType.YES}
                                                    control={<Radio sx={{ color: '#F39E4E' }} />}
                                                    label="Yes"
                                                    onClick={() => {
                                                        updatePricingConditions(
                                                            'gpvConditions',
                                                            SalesConditionsType.YES
                                                        );
                                                    }}
                                                />
                                                <FormControlLabel
                                                    value={SalesConditionsType.NO}
                                                    control={<Radio sx={{ color: '#F39E4E' }} />}
                                                    label="No"
                                                    onClick={() => {
                                                        updatePricingConditions(
                                                            'gpvConditions',
                                                            SalesConditionsType.NO
                                                        );
                                                    }}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                        {gpvConditions === SalesConditionsType.YES && (
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={6}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                marginTop: '20px',
                                                            }}
                                                        >
                                                            <Grid container>
                                                                <Grid item xs={4}>
                                                                    <Box sx={{ marginTop: '20px' }}>
                                                                        <label className="font-bold">
                                                                            Lowest
                                                                        </label>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Stack>
                                                                        <InputBaseField
                                                                            disabled={isPartner}
                                                                            name="gpvLowest"
                                                                            type="number"
                                                                        />
                                                                    </Stack>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                marginTop: '20px',
                                                            }}
                                                        >
                                                            <Grid container>
                                                                <Grid item xs={4}>
                                                                    <Box sx={{ marginTop: '20px' }}>
                                                                        <label className="font-bold">
                                                                            Highest
                                                                        </label>
                                                                    </Box>
                                                                </Grid>
                                                                <Grid item xs={6}>
                                                                    <Stack>
                                                                        <InputBaseField
                                                                            disabled={isPartner}
                                                                            name="gpvHighest"
                                                                            type="number"
                                                                        />
                                                                    </Stack>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                        <Typography variant="h3" sx={{ margin: '30px 0 0px 0' }}>
                                            Specified MCC
                                        </Typography>

                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                aria-labelledby="gpvConditions-billed-radio-buttons"
                                                name="mccConditions"
                                                row
                                                defaultValue={mccConditions}
                                            >
                                                <FormControlLabel
                                                    value={SalesConditionsType.YES}
                                                    control={<Radio sx={{ color: '#F39E4E' }} />}
                                                    label="Yes"
                                                    onClick={() => {
                                                        updatePricingConditions(
                                                            'mccConditions',
                                                            SalesConditionsType.YES
                                                        );
                                                    }}
                                                />
                                                <FormControlLabel
                                                    value={SalesConditionsType.NO}
                                                    control={<Radio sx={{ color: '#F39E4E' }} />}
                                                    label="No"
                                                    onClick={() => {
                                                        updatePricingConditions(
                                                            'mccConditions',
                                                            SalesConditionsType.NO
                                                        );
                                                    }}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                {mccConditions === SalesConditionsType.YES && (
                                    <Box sx={{ marginTop: '30px' }}>
                                        <NPIAutoComplete
                                            header="Merchant Category Code"
                                            name="mccs"
                                            multiple
                                            disabled={isPartner}
                                            options={mccData}
                                            placeholder="Select Merchant Category Code"
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </MainCard>
    );
};
